/**
 * Number formatting for Toss Securities values.
 *
 * The Toss Open API returns every price, quantity, amount and rate as a
 * DECIMAL STRING (`"72100"`, `"0.1516"`) — never a JSON number. We keep them as
 * strings end to end and only format at the render edge, so nothing ever passes
 * through an IEEE-754 double. That matters: `Number("9007199254740993")` is
 * already wrong, and money must not be approximately right.
 *
 * Grouping is therefore done on the digit string itself, not via
 * `Intl.NumberFormat(Number(v))`.
 */

/** A decimal value exactly as the Toss API sent it. */
export type Decimal = string;

/**
 * What a formatter accepts.
 *
 * `undefined` is in the type on purpose. Every value here comes off the network,
 * so a field the backend omits — or renames — legitimately arrives missing, and
 * each formatter already degrades that to a dash. Declaring `Decimal` alone made
 * the signature lie and pushed `?? ""` onto every call site.
 */
type Formattable = Decimal | undefined | null;

type Parsed = { sign: "" | "-"; int: string; frac: string };

/**
 * Split a decimal string into sign / integer digits / fraction digits.
 *
 * Accepts `unknown` on purpose. Everything here renders values that came off the
 * network, and a field the backend renamed or omitted arrives as `undefined`. When
 * this threw on `undefined.trim()` it took the whole screen down with a blank
 * page — a missing number should degrade to a dash, never to a white screen.
 */
function parse(value: unknown): Parsed | null {
  if (typeof value === "number" && Number.isFinite(value)) return parse(String(value));
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!/^[+-]?\d+(\.\d+)?$/.test(trimmed)) return null;
  const sign = trimmed.startsWith("-") ? "-" : "";
  const unsigned = trimmed.replace(/^[+-]/, "");
  const [int = "0", frac = ""] = unsigned.split(".");
  return { sign, int, frac };
}

/**
 * What to show when the value is not a decimal we recognise. A string we cannot
 * parse is surfaced as-is (better an odd-looking value than a silent zero); a
 * missing value becomes an em dash.
 */
function placeholder(value: unknown): string {
  return typeof value === "string" && value.trim() !== "" ? value : "—";
}

/** Insert thousands separators into a run of digits. */
function group(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Round a fraction string to `places`, carrying into the integer part when the
 * rounding pushes it over (e.g. `9.99` → 2 places → `9.99`, `9.999` → `10.00`).
 */
function round(parsed: Parsed, places: number): Parsed {
  if (places <= 0) {
    const roundUp = parsed.frac !== "" && Number(parsed.frac[0]) >= 5;
    if (!roundUp) return { ...parsed, frac: "" };
    return { ...parsed, int: bumpInteger(parsed.int), frac: "" };
  }
  if (parsed.frac.length <= places) {
    return { ...parsed, frac: parsed.frac.padEnd(places, "0") };
  }
  const kept = parsed.frac.slice(0, places);
  const roundUp = Number(parsed.frac[places]) >= 5;
  if (!roundUp) return { ...parsed, frac: kept };

  // Carry through the kept fraction digits, then into the integer part.
  const carried = (BigInt(kept) + 1n).toString();
  if (carried.length > places)
    return { ...parsed, int: bumpInteger(parsed.int), frac: "0".repeat(places) };
  return { ...parsed, frac: carried.padStart(places, "0") };
}

function bumpInteger(int: string): string {
  return (BigInt(int) + 1n).toString();
}

function render(parsed: Parsed): string {
  const body = parsed.frac === "" ? group(parsed.int) : `${group(parsed.int)}.${parsed.frac}`;
  return `${parsed.sign}${body}`;
}

/**
 * Format a decimal string with thousands separators.
 * `places` fixes the fraction length; omit it to keep the value as sent.
 * Returns the input untouched if it is not a decimal we recognise — we would
 * rather surface an odd-looking value than silently render `NaN`.
 */
export function formatDecimal(value: Formattable, places?: number): string {
  const parsed = parse(value);
  if (!parsed) return placeholder(value);
  return render(places === undefined ? parsed : round(parsed, places));
}

/** KRW is quoted in whole won. `72100` → `72,100원` */
export function formatKrw(value: Formattable): string {
  const parsed = parse(value);
  if (!parsed) return placeholder(value);
  return `${render(round(parsed, 0))}원`;
}

/** USD keeps two decimal places. `189.5` → `$189.50` */
export function formatUsd(value: Formattable): string {
  const parsed = parse(value);
  if (!parsed) return placeholder(value);
  const rounded = round(parsed, 2);
  return `${rounded.sign}$${group(rounded.int)}.${rounded.frac}`;
}

/** Format by the currency the API reported alongside the value. */
export function formatMoney(value: Formattable, currency: "KRW" | "USD"): string {
  return currency === "KRW" ? formatKrw(value) : formatUsd(value);
}

/**
 * 그 시장의 통화. 서버 `rules/sizing.py: currency_of_market` 과 같은 규약이에요.
 *
 * 한 곳에만 둡니다 — 화면마다 `market === "US" ? "USD" : "KRW"` 를 다시 쓰면 그중
 * 하나가 빠지고, 빠진 화면은 달러 금액에 "원" 을 붙여 1,300배 다른 뜻으로 읽힙니다.
 */
export function currencyOfMarket(market: "KR" | "US"): "KRW" | "USD" {
  return market === "US" ? "USD" : "KRW";
}

/**
 * Share quantities. KR is whole shares; US may be fractional, so trailing
 * zeros are trimmed rather than padded (`1.500` → `1.5`, `3.000` → `3`).
 */
export function formatQuantity(value: Formattable): string {
  const parsed = parse(value);
  if (!parsed) return placeholder(value);
  const frac = parsed.frac.replace(/0+$/, "");
  return render({ ...parsed, frac });
}

/**
 * Profit/loss RATIOS from the API are decimal fractions (`0.1516` = 15.16%).
 * Everything except conditional-order `targetProfitRate` uses this form.
 */
export function formatRate(ratio: Formattable, places = 2): string {
  const parsed = parse(ratio);
  if (!parsed) return placeholder(ratio);
  const shifted = shiftLeft(parsed, 2);
  return `${render(round(shifted, places))}%`;
}

/**
 * Conditional-order `targetProfitRate` is ALREADY a percent (`10.5` = +10.5%).
 * Kept as a separate function so the unit can never be guessed at a call site.
 */
export function formatPercent(percent: Formattable, places = 2): string {
  const parsed = parse(percent);
  if (!parsed) return placeholder(percent);
  return `${render(round(parsed, places))}%`;
}

/** Multiply by 10^places by moving the decimal point — no float involved. */
function shiftLeft(parsed: Parsed, places: number): Parsed {
  const frac = parsed.frac.padEnd(places, "0");
  const moved = frac.slice(0, places);
  const rest = frac.slice(places);
  const int = `${parsed.int}${moved}`.replace(/^0+(?=\d)/, "");
  return { sign: parsed.sign, int, frac: rest };
}

/** Prefix a non-negative value with `+` so gains and losses read symmetrically. */
export function withSign(formatted: string | undefined): string {
  if (typeof formatted !== "string" || formatted === "" || formatted === "—") return "—";
  return formatted.startsWith("-") ? formatted : `+${formatted}`;
}

/**
 * Which status token a P&L value should be painted with.
 * Korean markets colour gains red and losses blue, but the Wanted system's
 * semantic tokens are positive/negative — we map to the tokens and let the
 * design system own the actual hue.
 */
export function signTone(value: Formattable): "positive" | "negative" | "neutral" {
  const parsed = parse(value);
  if (!parsed) return "neutral";
  const isZero = /^0*$/.test(parsed.int) && /^0*$/.test(parsed.frac);
  if (isZero) return "neutral";
  return parsed.sign === "-" ? "negative" : "positive";
}
